import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { BG, C, St, FONT_BODY, GP_GLOBAL_CSS } from './theme';
import { EMPTY_DB, fetchDB, syncToSupabase, setSyncFn, saveDB } from './database';
import { DEFAULT_WIDGETS, WIDGETS_VERSION } from './constants';
import { setOpenExHistory } from './events';
import { ExerciseHistoryModal } from './components/Exercise';
import { ManualForm } from './components/Forms';
import { SchedaDetailSheet } from './components/Session';
import { DashboardPage } from './pages/DashboardPage';
import { HistoryPage } from './pages/HistoryPage';
import { EserciziPage } from './pages/EserciziPage';
import { AmiciPage } from './pages/AmiciPage';
import { CoachChatPage } from './pages/CoachChatPage';
import { AuthPage } from './pages/AuthPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { User, Group, Plus, BrainElectricity, StatsReport } from 'iconoir-react';
import { StatsPage } from './pages/StatsPage';
import { SchedePage } from './pages/SchedePage';
import { ImparaPage } from './pages/ImparaPage';
import { FolderIcon } from './components/Folder';
const SHOW_TEST = false;

const TAB_ICONS = {
  io: (color) => <User width={24} height={24} color={color} strokeWidth={1.7} />,
  amici: (color) => <Group width={24} height={24} color={color} strokeWidth={1.7} />,
  stats: (color) => <StatsReport width={24} height={24} color={color} strokeWidth={1.7} />,
  impara: (color) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3L2 8l10 5 10-5-10-5z"/>
      <path d="M2 8v6"/>
      <path d="M6 10.5v5c0 1.5 2.686 3 6 3s6-1.5 6-3v-5"/>
    </svg>
  ),
};

const TABS = [
  { id: "io", label: "Home", color: "#007AFF" },
  { id: "amici", label: "Amici", color: "#34C759" },
  { id: "stats", label: "Stats", color: "#AF52DE" },
  { id: "impara", label: "Impara", color: "#FF9500" },
];

export default function App() {
  const [page, setPage] = useState("io");
  const [db, setDB] = useState(EMPTY_DB);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [replayOnboarding, setReplayOnboarding] = useState(false);
  const [historyEx, setHistoryEx] = useState(null);
  const [showLauncher, setShowLauncher] = useState(false);
  const [showCreateScheda, setShowCreateScheda] = useState(false);
  const [showManualSheet, setShowManualSheet] = useState(false);
  const [manualSaved, setManualSaved] = useState(false);
  const [coachInitScheda, setCoachInitScheda] = useState(null); // { name, ts } per forzare re-trigger
  const [manualInitPhase, setManualInitPhase] = useState(null);
  const [coachAvviaMode, setCoachAvviaMode] = useState(false);
  const manualFormRef = useRef(null);
  const prevDbRef = useRef(null);
  const [widgets, setWidgets] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('gp_home_widgets'));
      if (s && s.__v === WIDGETS_VERSION) return { ...DEFAULT_WIDGETS, ...s };
      return DEFAULT_WIDGETS;
    } catch { return DEFAULT_WIDGETS; }
  });
  const saveWidgets = w => { setWidgets(w); localStorage.setItem('gp_home_widgets', JSON.stringify({ ...w, __v: WIDGETS_VERSION })); };

  useEffect(() => {
    setOpenExHistory(setHistoryEx);
    return () => { setOpenExHistory(null); };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setLoading(false); setLoadTimeout(true); }, 8000);
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(t);
      setUser(session?.user ?? null);
      if (!session) setLoading(false);
    }).catch(() => { clearTimeout(t); setLoading(false); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED / USER_UPDATED keep the same user — skip re-fetch to avoid
      // overwriting local state that is still mid-sync with Supabase
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') return;
      clearTimeout(t);
      setUser(session?.user ?? null);
      if (!session) { setDB(EMPTY_DB); prevDbRef.current = null; setSyncFn(null); setLoading(false); }
    });
    return () => { subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (!user) { return; }
    // 1. Optimistic check: user-scoped localStorage key
    if (localStorage.getItem(`gp_onboarding_v1_${user.id}`)) {
      setOnboardingDone(true);
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => { if (!cancelled) { setLoading(false); } }, 8000);
    fetchDB(user.id).then(async data => {
      clearTimeout(t);
      if (cancelled) return;
      setDB(data);
      prevDbRef.current = data;
      setSyncFn((newDb) => {
        const prev = prevDbRef.current || EMPTY_DB;
        prevDbRef.current = newDb;
        return syncToSupabase(prev, newDb, user.id);
      });
      // 2. Server-side truth: profile with display_name = onboarding completed
      try {
        const { data: profile } = await supabase.from('profiles')
          .select('display_name').eq('user_id', user.id).single();
        if (!cancelled && profile?.display_name) {
          localStorage.setItem(`gp_onboarding_v1_${user.id}`, 'done');
          setOnboardingDone(true);
        }
      } catch { }
      if (!cancelled) setLoading(false);
    }).catch((err) => { console.error('[App] fetchDB failed:', err); clearTimeout(t); if (!cancelled) setLoading(false); });
    return () => { cancelled = true; clearTimeout(t); setSyncFn(null); };
  }, [user]);

  if (loading) return (
    <div style={{ fontFamily: FONT_BODY, background: BG, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: "0 32px", WebkitFontSmoothing: "antialiased" }}>
      {loadTimeout ? (
        <>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.text, textAlign: "center" }}>Impossibile connettersi</div>
          <div style={{ fontSize: 14, color: C.subtle, textAlign: "center", lineHeight: 1.55 }}>Verifica la connessione e ricarica la pagina.</div>
          <button onClick={() => window.location.reload()} style={{ padding: "11px 24px", borderRadius: 12, border: "none", cursor: "pointer", background: C.blue, color: "#fff", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>Ricarica</button>
        </>
      ) : (
        <div className="gp-loader" />
      )}
    </div>
  );

  if (!user) return <AuthPage />;

  if (!onboardingDone) return (
    <OnboardingPage
      user={user}
      db={db}
      setDB={setDB}
      onComplete={() => { localStorage.setItem(`gp_onboarding_v1_${user.id}`, 'done'); setOnboardingDone(true); }}
    />
  );

  if (replayOnboarding) return (
    <OnboardingPage
      user={user}
      db={db}
      setDB={setDB}
      replayMode
      onComplete={() => setReplayOnboarding(false)}
    />
  );

  if (SHOW_TEST) return null;

  const TabBtn = ({ tab }) => {
    const active = page === tab.id;
    const col = active ? C.text : C.subtle;
    return (
      <button key={tab.id} onClick={() => setPage(tab.id)}
        style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
          padding: "10px 20px", borderRadius: 100, border: "none", cursor: "pointer",
          background: active ? "rgba(120,120,128,0.12)" : "transparent",
          transition: "background 0.15s", fontFamily: FONT_BODY,
          WebkitTapHighlightColor: "transparent",
        }}>
        {TAB_ICONS[tab.id](col)}
        <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, color: col, lineHeight: 1 }}>{tab.label}</span>
      </button>
    );
  };

  return (
    <div style={page === 'chat' ? { ...St.page, paddingBottom: 0, minHeight: 'unset', height: '100dvh', overflow: 'hidden' } : St.page}>
      <style>{GP_GLOBAL_CSS}</style>
      {historyEx && <ExerciseHistoryModal name={historyEx} sessions={db.sessions} onClose={() => setHistoryEx(null)} />}
      {page === "io" && <DashboardPage db={db} setDB={setDB} user={user} onStartSession={name => setShowManualSheet(name || "")} onGoToCoach={name => { setCoachInitScheda({ name, ts: Date.now() }); setPage('chat'); }} onReplayOnboarding={() => setReplayOnboarding(true)} onGoToEsercizi={() => setPage('esercizi')} onGoToSchede={() => setPage('schede')} onOpenLauncher={() => setShowLauncher(true)} onGoToCronologia={() => setPage('cronologia')} onGoToExercise={name => setHistoryEx(name)} widgets={widgets} saveWidgets={saveWidgets} />}
      {page === "cronologia" && <HistoryPage db={db} setDB={setDB} onBack={() => setPage('io')} />}
      {page === "esercizi" && <EserciziPage db={db} onBack={() => setPage('io')} />}
      {page === "schede" && <SchedePage db={db} setDB={setDB} onBack={() => setPage('io')} />}
      {page === "amici" && <AmiciPage user={user} db={db} />}
      {page === "stats" && <StatsPage db={db} />}
      {page === "impara" && <ImparaPage />}
      {page === "chat" && <CoachChatPage db={db} user={user} autoSchedaName={coachInitScheda} onConsumeAutoScheda={() => setCoachInitScheda(null)} onStartSession={name => { setManualInitPhase(null); setShowManualSheet(name || ""); }} onAvviaMode={v => setCoachAvviaMode(v)} onBack={() => setPage('io')} />}

      {/* Launcher sheet — scegli azione */}
      {showLauncher && (() => {
        const canLoad = (db.schede || []).some(s => s.exercises?.length > 0);
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.40)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
            onClick={e => { if (e.target === e.currentTarget) setShowLauncher(false); }}>
            <div style={{
              background: '#ffffff', borderRadius: "36px 36px 0 0", width: "100%", maxWidth: 560,
              margin: "0 auto", paddingBottom: "max(28px,env(safe-area-inset-bottom,16px))",
            }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(120,120,128,0.28)", margin: "12px auto 0" }} />
              <div style={{ padding: "16px 20px 12px", fontSize: 17, fontWeight: 700, color: C.text, textAlign: "center" }}>Cosa vuoi fare?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
                {/* Card 1 — Carica sessione */}
                {canLoad ? (
                  <button
                    onClick={() => { setShowLauncher(false); setManualInitPhase('import'); setShowManualSheet(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 16,
                      padding: "14px 18px", borderRadius: 20,
                      border: "none", cursor: "pointer",
                      background: "rgba(120,120,128,0.07)",
                      fontFamily: FONT_BODY, textAlign: "left", width: "100%",
                    }}>
                    <img
                      src={process.env.PUBLIC_URL + '/dumbbell.png'}
                      alt="sessione"
                      style={{ width: 56, height: 56, objectFit: "contain", flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2 }}>Carica sessione</div>
                      <div style={{ fontSize: 13, color: C.subtle }}>Registra un nuovo allenamento</div>
                    </div>
                  </button>
                ) : (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    padding: "20px 18px", borderRadius: 20,
                    background: "rgba(120,120,128,0.07)", textAlign: "center",
                  }}>
                    <img
                      src={process.env.PUBLIC_URL + '/dumbbell.png'}
                      alt="sessione"
                      style={{ width: 48, height: 48, objectFit: "contain", opacity: 0.4, marginBottom: 10 }}
                    />
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4, fontFamily: FONT_BODY }}>Nessuna scheda ancora</div>
                    <div style={{ fontSize: 13, color: C.subtle, lineHeight: 1.5, marginBottom: 14, fontFamily: FONT_BODY }}>
                      Crea una scheda con i tuoi esercizi per iniziare a registrare le sessioni.
                    </div>
                    <button
                      onClick={() => { setShowLauncher(false); setShowCreateScheda(true); }}
                      style={{
                        padding: "11px 24px", borderRadius: 12,
                        border: "none", cursor: "pointer",
                        background: C.blue, color: "#fff",
                        fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700,
                      }}>
                      Crea la prima scheda
                    </button>
                  </div>
                )}
                {/* Card 2 — Crea scheda */}
                <button
                  onClick={() => { setShowLauncher(false); setShowCreateScheda(true); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 16,
                    padding: "14px 18px", borderRadius: 20,
                    border: "none", cursor: "pointer",
                    background: "rgba(120,120,128,0.07)",
                    fontFamily: FONT_BODY, textAlign: "left", width: "100%",
                  }}>
                  <div style={{ flexShrink: 0 }}>
                    <FolderIcon accentColor={C.blue} size={56} />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2 }}>Crea scheda</div>
                    <div style={{ fontSize: 13, color: C.subtle }}>Organizza i tuoi esercizi</div>
                  </div>
                </button>
                {/* Card 3 — Parla con il Coach (coming soon) */}
                <div style={{
                    display: "flex", flexDirection: "column", gap: 4,
                  }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 16,
                    padding: "14px 18px", borderRadius: 20,
                    background: "rgba(120,120,128,0.07)",
                    opacity: 0.45,
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 16, flexShrink: 0,
                      background: `${C.amber}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <BrainElectricity width={28} height={28} color={C.amber} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2, fontFamily: FONT_BODY }}>Parla con il Coach</div>
                      <div style={{ fontSize: 13, color: C.subtle, fontFamily: FONT_BODY }}>Analisi AI del tuo allenamento</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: C.subtle, textAlign: 'center', fontFamily: FONT_BODY }}>
                    Funzionalita in arrivo
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sheet crea scheda — SchedaDetailSheet gestisce il proprio overlay */}
      {showCreateScheda && (
        <SchedaDetailSheet
          scheda={null}
          db={db}
          onSave={newDb => { setDB(newDb); saveDB(newDb); setShowCreateScheda(false); }}
          onClose={() => setShowCreateScheda(false)}
        />
      )}

      {/* Fullpage nuova sessione */}
      {showManualSheet !== false && (
        <div style={{
          position: "fixed", inset: 0, background: "#fff", zIndex: 200,
          display: "flex", flexDirection: "column",
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}>
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: manualSaved ? "24px 20px 0" : "16px 20px 0" }}>
            <ManualForm ref={manualFormRef} db={db} onSave={u => { setDB(u); saveDB(u); }} onClose={() => { setShowManualSheet(false); setManualSaved(false); setManualInitPhase(null); }} onSaved={() => setManualSaved(true)} initialScheda={typeof showManualSheet === 'string' ? showManualSheet : ''} initialPhase={manualInitPhase} />
          </div>
        </div>
      )}

      {/* Floating bottom bar */}
      {!coachAvviaMode && <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        paddingLeft: 16, paddingRight: 16,
        zIndex: 100, pointerEvents: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 10,
      }}>
        {/* Tab pill — glass */}
        <div style={{
          display: "flex", alignItems: "center",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderRadius: 100,
          boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.06)",
          padding: "10px 12px",
          pointerEvents: "auto",
          flex: 1, maxWidth: 560,
        }}>
          {TABS.map(t => <TabBtn key={t.id} tab={t} />)}
        </div>
        {/* FAB — stessa altezza della pill */}
        <button
          onClick={() => setShowLauncher(true)}
          style={{
            flexShrink: 0,
            width: 78, height: 78,
            borderRadius: "50%",
            border: "none",
            background: "#ffffff",
            backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.14), 0 0 0 0.5px rgba(0,0,0,0.06)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "auto",
            WebkitTapHighlightColor: "transparent",
          }}>
          <Plus width={26} height={26} color={C.text} strokeWidth={2.2} />
        </button>
      </div>}
    </div>
  );
}
